import React from "react";
import { MathJax } from "better-react-mathjax";

export function GaussIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Systems of linear equations form one of the cornerstones of applied mathematics. Whenever several
      unknown quantities are related by linear relationships, we obtain a linear system. In compact form such
      a system is written as <MathJax inline>{"\\( A x = b \\)"}</MathJax>, 
      where <MathJax inline>{"\\( A \\in \\mathbb{R}^{m\\times n} \\)"}</MathJax> is 
      the coefficient matrix, <MathJax inline>{"\\( x \\in \\mathbb{R}^{n} \\)"}</MathJax> is the vector of unknowns, 
      and <MathJax inline>{"\\( b \\in \\mathbb{R}^{m} \\)"}</MathJax> is the right-hand side. Such systems
      appear in countless applications, from engineering models to data science algorithms. Their efficient
      solution is therefore not only of theoretical interest but also of great practical importance.
    </div>
  );
}

export default function GaussLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      {/* ---------------- NEW EXTENDED EXPLANATION INSERT ---------------- */}
      <h2 className="text-sm font-semibold md:text-base">1. Introduction and Motivation</h2>
      <p>Why do we care about such systems? Because they appear literally everywhere:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Engineering and Physics:</strong> Discretized PDEs (heat transfer, vibrations, fluid flow, electromagnetism) lead to large linear systems.</li>
        <li><strong>Computer Science:</strong> Graphics, image reconstruction, and machine learning often solve <MathJax inline>{"\\( A x = b \\)"}</MathJax> as a subproblem.</li>
        <li><strong>Data Science and Statistics:</strong> Linear regression, least squares, polynomial fitting reduce to solving <MathJax inline>{"\\( A x = b \\)"}</MathJax>.</li>
        <li><strong>Economics and Social Sciences:</strong> Input–output models, optimization, and network analysis rely on linear systems.</li>
      </ul>
      <p>
        Because of this universality, an efficient and reliable method to solve linear systems is fundamental.
        Among many approaches, Gaussian elimination is the classical and widely taught algorithm:
        systematic, reliable with pivoting, and foundational for advanced techniques.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">2. Linear Systems of Equations</h2>
      <p>
        A linear equation in variables <MathJax inline>{"\\( x_1, x_2, \\ldots, x_n \\)"}</MathJax> has the form {" "}
        <MathJax>{"\\[ a_1 x_1 + a_2 x_2 + \\cdots + a_n x_n = b. \\]"}</MathJax>
      </p>
      <p>
        A system of linear equations consists of several such equations considered simultaneously. For instance, in three unknowns:
      </p>
      <MathJax>{String.raw`\[\begin{aligned}
        a_{11}x_1 + a_{12}x_2 + a_{13}x_3 &= b_1,\\
        a_{21}x_1 + a_{22}x_2 + a_{23}x_3 &= b_2,\\
        a_{31}x_1 + a_{32}x_2 + a_{33}x_3 &= b_3.
      \end{aligned}\]`}</MathJax>
      <p>In matrix notation, this is simply <MathJax inline>{"\\( A x = b \\)"}</MathJax>, where</p>
      <MathJax>{String.raw`\[ A=\begin{bmatrix} a_{11} & a_{12} & a_{13} \\ a_{21} & a_{22} & a_{23} \\ a_{31} & a_{32} & a_{33} \end{bmatrix},\quad x=\begin{bmatrix}x_1\\ x_2\\ x_3\end{bmatrix},\quad b=\begin{bmatrix}b_1\\ b_2\\ b_3\end{bmatrix}. \]`}</MathJax>
      <p>To manipulate the system efficiently, form the augmented matrix</p>
      <MathJax>{String.raw`\[ [A\mid b] = \begin{bmatrix} a_{11} & a_{12} & a_{13} &\mid& b_1 \\ a_{21} & a_{22} & a_{23} &\mid& b_2 \\ a_{31} & a_{32} & a_{33} &\mid& b_3 \end{bmatrix}. \]`}</MathJax>
      <p>This notation allows systematic row operations without writing variables each time.</p>

      <h3 className="font-semibold mt-3">Homogeneous and Inhomogeneous Systems</h3>
      <p>
        A system is <em>homogeneous</em> if <MathJax inline>{"\\( b=0 \\)"}</MathJax>, i.e., <MathJax inline>{"\\( A x = 0 \\)"}</MathJax>. The trivial solution <MathJax inline>{"\\( x=0 \\)"}</MathJax> always exists; additional non-trivial solutions depend on the rank of <MathJax inline>{"\\( A \\)"}</MathJax>.
      </p>
      <p>
        A system is <em>inhomogeneous</em> if <MathJax inline>{"\\( b\\neq 0 \\)"}</MathJax>. Existence of a solution depends on the relationship between <MathJax inline>{"\\( A \\)"}</MathJax> and <MathJax inline>{"\\( b \\)"}</MathJax>.
      </p>

      <h3 className="font-semibold mt-3">Geometric Interpretation</h3>
      <p>For two equations with two unknowns, each equation is a line in <MathJax inline>{"\\( \\mathbb{R}^{2} \\)"} </MathJax> ; the solution is their intersection:</p>
      <ul className="list-disc pl-6">
        <li>Unique solution: lines intersect at a single point.</li>
        <li>No solution: lines are parallel and distinct.</li>
        <li>Infinitely many: lines coincide.</li>
      </ul>
      <p>In higher dimensions, equations define hyperplanes; the solution set is their intersection.</p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">3. Existence and Number of Solutions</h2>
      <h3 className="font-semibold mt-2">The Rank Criterion</h3>
      <p>
        The rank <MathJax inline>{"\\( \\mathrm{rank}(A) \\)"}</MathJax> is the dimension of the span of rows/columns. It counts the number of independent equations.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          If <MathJax inline>{"\\( \\mathrm{rank}(A) < \\mathrm{rank}([A \\;|\\; b]) \\)"}</MathJax>: inconsistent (no solution).
        </li>
        <li>
          If <MathJax inline>{"\\( \\mathrm{rank}(A) = \\mathrm{rank}([A \\;|\\; b]) = n \\)"}</MathJax>: unique solution.
        </li>
        <li>
          If <MathJax inline>{"\\( \\mathrm{rank}(A) = \\mathrm{rank}([A \\;|\\; b]) < n \\)"}</MathJax>: infinitely many solutions (free variables).
        </li>
      </ul>
      <p>
        For homogeneous systems <MathJax inline>{"\\( A x = 0 \\)"} </MathJax>: if <MathJax inline>{"\\( \\mathrm{rank}(A)=n \\)"} </MathJax>
        then only <MathJax inline>{"\\( x=0 \\)"}</MathJax> is a valid solution. If <MathJax inline>{"\\( \\mathrm{rank}(A)<n \\)"} </MathJax>
        then infinitely many solutions exist.
      </p>


      <h3 className="font-semibold mt-2">Examples</h3>
      <div className="mt-2 space-y-4">
        <div>
          <p><strong>Example 1 (No Solution).</strong></p>
          <MathJax>{String.raw`
      \[
        \begin{aligned}
          x+y &= 1 \\
          2x+2y &= 3
        \end{aligned}
      \]
    `}</MathJax>
          <p>
            Then <MathJax inline>{"\\( \\mathrm{rank}(A)=1 \\)"}</MathJax> but{" "}
            <MathJax inline>{"\\( \\mathrm{rank}([A\\;|\\; b])=2 \\)"}</MathJax>.
          </p>
        </div>

        <div>
          <p><strong>Example 2 (Unique Solution).</strong></p>
          <MathJax>{String.raw`
      \[
        \begin{aligned}
          x+y &= 1 \\
          x-y &= 0
        \end{aligned}
      \]
    `}</MathJax>
          <p>⇒ <MathJax inline>{"\\( x=y=1/2 \\)"}</MathJax>.</p>
        </div>

        <div>
          <p><strong>Example 3 (Infinitely Many).</strong></p>
          <MathJax>{String.raw`
      \[
        \begin{aligned}
          x+y &= 1 \\
          2x+2y &= 2
        \end{aligned}
      \]
    `}</MathJax>
          <p>⇒ same line; ranks both 1 &lt; 2.</p>
        </div>
      </div>


      <h2 className="text-sm font-semibold mt-4 md:text-base"> 4. Elementary Row Operations</h2>
      <h3 className="font-semibold mt-2">There are three types of row operations</h3>
      <ul className="list-disc pl-6">
        <li>Row swap: <MathJax inline>{"\\( R_i \\leftrightarrow R_j \\)"}</MathJax>.</li>
        <li>Scaling: <MathJax inline>{"\\( R_i \\leftarrow \\alpha R_i, where \\ \\alpha\\neq 0 \\)"}</MathJax>.</li>
        <li>Row replacement: <MathJax inline>{"\\( R_i \\leftarrow R_i - \\mu R_k \\)"}</MathJax>.</li>
      </ul>
      <p>These operations preserve the set of solutions, because the order of the equations doesn’t matter,
        scaling keeps the same hyperplane, and linear combinations of equations also preserve solution sets.
        From a matrix point of view Row operations correspond to left-multiplication by invertible elementary matrices (permutations, scalings, shears).</p>
      <h3 className="font-semibold mt-2">Pivoting: The Need to Swap Rows</h3>
      <p>To avoid zero pivots and numerical instalibity, partial pivoting can be applied. Zero pivots can become a problem since the essence of Gaussian Algorithm
        is the elimination of variables using the pivot equation. If the pivot is zero, we cannot eliminate the variable in rows below.
        Even if the pivot is very small, dividing by it can amplify numerical errors. Thus, we search for the largest absolute entry in the pivot column below (and including) the current row, and swap rows if needed.
      </p>
      <h2 className="text-sm font-semibold mt-4 md:text-base">5. The Gaussian Elimination Algorithm</h2>
      The algorithm consists of two main phases: forward elimination to convert the system into upper-triangular form, and back substitution to solve for the unknowns.
      <h3 className="font-semibold mt-2">The Idea of Elimination</h3>
      <p>
        The core idea is: use the current pivot row (equation) to eliminate its leading variable from all rows below.
        For column <MathJax inline>{"\\( k \\)"}</MathJax>, subtract a suitable multiple of the pivot row from each lower
        row so that all entries below the pivot become zero. This step is repeated for each column, moving left to right,
        reducing the system to upper-triangular form.
      </p>
      <h3 className="font-semibold mt-2">Forward Elimination Step by Step</h3>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Choose pivot in column <MathJax inline>{"\\( k \\)"}</MathJax> (largest absolute entry in rows <MathJax inline>{"\\( k...n \\)"}</MathJax>), swap.</li>
        <li>For each <MathJax inline>{"\\( i>k \\)"}</MathJax>, set <MathJax inline>{"\\( \\ell_{ik} = a_{ik}/a_{kk} \\)"}</MathJax> and do <MathJax inline>{"\\( R_i \\leftarrow R_i - \\ell_{ik} R_k \\)"}</MathJax>.</li>
      </ol>
      <h3 className="font-semibold mt-2">Back Substitution</h3>
      <p>
        During the back substitution phase, we solve for the unknowns starting from the last equation and moving upwards.
        Each equation has one less unknown than the previous, allowing us to substitute already found values.
        For <MathJax inline>{"\\( i=n,\\dots,1 \\)"}</MathJax>we set <MathJax inline>{"\\( x_i = (b_i - \n\\sum_{j=i+1}^n a_{ij}x_j)/a_{ii} \\)"}</MathJax>.
        assuming that the pivots <MathJax inline>{"\\( a_{ii} \\)"}</MathJax> are non-zero.
      </p>

      <h3 className="font-semibold mt-2">Pivoting and Numerical Stability and Complexity</h3>
      <p>
        Pivoting is crucial for numerical stability. Without it, small or zero pivots can lead to large rounding errors or failure to proceed.
        Partial pivoting (row swaps) is usually sufficient; full pivoting (also swapping columns) is more stable but rarely needed.
        The algorithm's complexity is dominated by the forward elimination phase.
      </p>

      <h4 className="font-semibold mt-2">Computation of Complexity</h4>
      <p>
        The computational complexity of Gaussian elimination is determined by counting the number of arithmetic operations (additions, subtractions, multiplications, divisions) required to reduce an <MathJax inline>{"\\( n \\times n \\)"}</MathJax> system to upper-triangular form and then solve it by back substitution.
      </p>
      <ul className="list-disc pl-6">
        <li>
          <strong>Forward elimination:</strong> For each pivot row <MathJax inline>{"\\( k=1 \\)"}</MathJax> to <MathJax inline>{"\\( n-1 \\)"}</MathJax>, we eliminate entries below the pivot in column <MathJax inline>{"\\( k \\)"}</MathJax>. For each such elimination, about <MathJax inline>{"\\( n-k \\)"}</MathJax> rows and <MathJax inline>{"\\( n-k \\)"}</MathJax> columns are involved. Summing over all steps, the total number of operations is approximately
          <MathJax>{String.raw`\[
            \sum_{k=1}^{n-1} (n-k)^2 = \frac{n^3}{3} - \frac{n^2}{2} + \frac{n}{6}
          \]`}</MathJax>
          which is <MathJax inline>{"\\( O(n^3) \\)"}</MathJax>.
        </li>
        <li>
          <strong>Back substitution:</strong> Once in upper-triangular form, solving for each variable requires summing up to <MathJax inline>{"\\( n \\)"}</MathJax> terms, for a total of about <MathJax inline>{"\\( \\frac{n^2}{2} \\)"}</MathJax> operations, i.e., <MathJax inline>{"\\( O(n^2) \\)"}</MathJax>.
        </li>
      </ul>
      <p>
        Thus, the overall complexity is dominated by the forward elimination phase, yielding a total of approximately <MathJax inline>{"\\( \\frac{2}{3} n^3 \\)"}</MathJax> floating point operations (flops) for large <MathJax inline>{"\\( n \\)"}</MathJax>. In summary:
      </p>
      <ul className="list-disc pl-6">
        <li>Forward elimination: <MathJax inline>{"\\( \\sim \\frac{2}{3} n^3 \\)"}</MathJax> flops</li>
        <li>Back substitution: <MathJax inline>{"\\( \\sim \\frac{1}{2} n^2 \\)"}</MathJax> flops</li>
        <li><strong>Total:</strong> <MathJax inline>{"\\( O(n^3) \\)"}</MathJax></li>
      </ul>
      <p>
        This cubic complexity means Gaussian elimination is practical for moderate-sized dense systems (up to a few thousand unknowns), but for very large or sparse systems, more specialized or iterative methods are preferred.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">6. Special Cases and Observations</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Zero rows:</strong> <MathJax inline>{"\\( [0\ \\; 0\ \\; 0 \\;|\\; 0] \\)"}</MathJax> ⇒ redundancy.</li>
        <li><strong>Contradictory rows:</strong> <MathJax inline>{"\\( [0\ \\; 0\ \\; 0 \\;|\\; c],\\ c\\neq 0 \\)"}</MathJax> ⇒ no solution.</li>
        <li><strong>Free variables:</strong> non-pivot columns ⇒ infinitely many solutions (affine subspace).</li>
        <li><strong>Geometric view:</strong> intersection of hyperplanes: point / empty / line/plane/…</li>
      </ul>
      <h2 className="text-sm font-semibold mt-4 md:text-base">8. Example</h2>
      <div>
        <p><strong>4×4 System with Unique Solution.</strong></p>
        <MathJax>{String.raw`
\[
\begin{aligned}
A &= 
\begin{bmatrix}
2 & 1 & -1 & 3 \\
4 & 1 & 2 & 0 \\
-2 & 3 & 1 & 5 \\
6 & -1 & 2 & 4
\end{bmatrix}, \quad
b =
\begin{bmatrix}
5 \\ 7 \\ 7 \\ 11
\end{bmatrix}.
\end{aligned}
\]
`}</MathJax>

        <p><em>Initial augmented matrix:</em></p>
        <MathJax>{String.raw`
\[
\left[\begin{array}{rrrr|r}
2 & 1 & -1 & 3 & 5 \\
4 & 1 & 2 & 0 & 7 \\
-2 & 3 & 1 & 5 & 7 \\
6 & -1 & 2 & 4 & 11
\end{array}\right]
\]
`}</MathJax>

        <p><em>Column 1 (pivot = 6 in row 4): swap and eliminate.</em></p>
        <MathJax>{String.raw`
\[
\left[\begin{array}{rrrr|r}
6 & -1 & 2 & 4 & 11 \\
0 & \tfrac{5}{3} & \tfrac{2}{3} & -\tfrac{8}{3} & -\tfrac{1}{3} \\
0 & \tfrac{8}{3} & \tfrac{5}{3} & \tfrac{19}{3} & \tfrac{32}{3} \\
0 & \tfrac{4}{3} & -\tfrac{5}{3} & \tfrac{5}{3} & \tfrac{4}{3}
\end{array}\right]
\]
`}</MathJax>

        <p><em>Column 2 (pivot = 8/3 in row 3): swap and eliminate.</em></p>
        <MathJax>{String.raw`
\[
\left[\begin{array}{rrrr|r}
6 & -1 & 2 & 4 & 11 \\
0 & \tfrac{8}{3} & \tfrac{5}{3} & \tfrac{19}{3} & \tfrac{32}{3} \\
0 & 0 & -\tfrac{3}{8} & -\tfrac{53}{8} & -7 \\
0 & 0 & -\tfrac{5}{2} & -\tfrac{3}{2} & -4
\end{array}\right]
\]
`}</MathJax>

        <p><em>Column 3 (pivot = -5/2 in row 4): swap and eliminate.</em></p>
        <MathJax>{String.raw`
\[
\left[\begin{array}{rrrr|r}
6 & -1 & 2 & 4 & 11 \\
0 & \tfrac{8}{3} & \tfrac{5}{3} & \tfrac{19}{3} & \tfrac{32}{3} \\
0 & 0 & -\tfrac{5}{2} & -\tfrac{3}{2} & -4 \\
0 & 0 & 0 & -\tfrac{32}{5} & -\tfrac{32}{5}
\end{array}\right]
\]
`}</MathJax>

        <p><em>Back substitution:</em></p>
        <MathJax>{String.raw`
\[
\begin{aligned}
-\tfrac{32}{5}x_4 &= -\tfrac{32}{5} \quad \Rightarrow \quad x_4=1, \\
-\tfrac{5}{2}x_3 - \tfrac{3}{2}(1) &= -4 \quad \,\,\,\, \Rightarrow \quad x_3=1, \\
\tfrac{8}{3}x_2 + \tfrac{5}{3}(1) + \tfrac{19}{3}(1) &= \tfrac{32}{3} \quad \,\,\,\,\, \Rightarrow \quad x_2=1, \\
6x_1 - 1 + 2 + 4 &= 11 \quad \,\,\,\,\,\, \Rightarrow \quad x_1=1.
\end{aligned}
\]
`}</MathJax>

        <p><strong>Solution:</strong> <MathJax inline>{"\\((x_1,x_2,x_3,x_4)=(1,1,1,1)\\)"}</MathJax></p>
      </div>



    </div>
  );
}
